import { useList, useGo, useDelete, useCustomMutation } from "@refinedev/core";
import { useState } from "react";
import {
  Plus,
  Eye,
  Megaphone,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  Ban,
} from "lucide-react";
import { notify } from '@/lib/notify';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Broadcast, BroadcastStatus } from "./types";
import { CHANNEL_DISPLAY_NAMES } from './components/constants';
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";

/**
 * Broadcasts list page with DataTable
 */
export function BroadcastsList() {
  const go = useGo();
  const { mutate: deleteBroadcast } = useDelete();
  const { mutate: cancelMutate } = useCustomMutation();
  useScrollRestoration();
  const [deleteItems, setDeleteItems] = useState<Broadcast[]>([]);
  const [page, setPage] = usePersistedState("broadcasts:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("broadcasts:sort", { field: "createdAt", order: "desc" });
  const [search, setSearch] = usePersistedState("broadcasts:search", "");
  const [statusFilter, setStatusFilter] = usePersistedState<BroadcastStatus | ''>("broadcasts:statusFilter", '');
  const pageSize = 10;

  const filters = [
    ...(search ? [{ field: "search" as const, operator: "contains" as const, value: search }] : []),
    ...(statusFilter ? [{ field: "status" as const, operator: "eq" as const, value: statusFilter }] : []),
  ];

  const listResult = useList<Broadcast>({
    resource: "broadcasts",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result?.data ?? [];
  const total = listResult.result?.total ?? 0;
  const isLoading = listResult.query?.isLoading ?? false;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "DRAFT":
        return <Badge variant="outline">Черновик</Badge>;
      case "SCHEDULED":
        return (
          <Badge className="bg-blue-500/10 text-blue-500">
            <Clock className="h-3 w-3 mr-1" />
            Запланирована
          </Badge>
        );
      case "SENDING":
        return (
          <Badge className="bg-yellow-500/10 text-yellow-500">
            <Send className="h-3 w-3 mr-1 animate-pulse" />
            Отправка
          </Badge>
        );
      case "COMPLETED":
        return (
          <Badge className="bg-green-500/10 text-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Завершена
          </Badge>
        );
      case "FAILED":
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Ошибка
          </Badge>
        );
      case "CANCELLED":
        return <Badge variant="secondary">Отменена</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {return "—";}
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getProgress = (broadcast: Broadcast) => {
    if (broadcast.totalRecipients === 0) {return "—";}
    const sent = broadcast.sentCount + broadcast.failedCount;
    const percent = Math.round((sent / broadcast.totalRecipients) * 100);
    return `${broadcast.sentCount}/${broadcast.totalRecipients} (${percent}%)`;
  };

  const columns: Column<Broadcast>[] = [
    {
      key: "id",
      header: "ID",
      width: "60px",
      sortable: true,
    },
    {
      key: "name",
      header: "Название",
      sortable: true,
      render: (_, broadcast) => (
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{broadcast.name}</span>
        </div>
      ),
    },
    {
      key: "channels",
      header: "Каналы",
      render: (_, broadcast) => (
        <div className="flex flex-wrap gap-1">
          {broadcast.channels.map((channel) => (
            <Badge key={channel} variant="outline">
              {CHANNEL_DISPLAY_NAMES[channel] ?? channel}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "status",
      header: "Статус",
      sortable: true,
      render: (value) => getStatusBadge(String(value)),
    },
    {
      key: "progress",
      header: "Прогресс",
      render: (_, broadcast) => getProgress(broadcast),
    },
    {
      key: "emailOpens",
      header: "Открыто (email)",
      width: "140px",
      render: (_, broadcast) => {
        // Колонка показывается всегда, но имеет смысл только когда в каналах EMAIL.
        const hasEmail = (broadcast.channels as readonly string[]).includes('EMAIL');
        if (!hasEmail) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        const opened = broadcast.emailOpenedRecipients ?? 0;
        const total = broadcast.emailTotalOpens ?? 0;
        if (opened === 0) {
          return <span className="text-muted-foreground text-sm">0</span>;
        }
        // total ≥ opened, поскольку opened считается по уникальным получателям.
        // Показываем «N (всего hit'ов M)» только если есть повторные открытия.
        return (
          <span className="text-sm font-medium" title={`Уникально: ${opened}, всего hit'ов: ${total}`}>
            {opened}
            {total > opened && (
              <span className="text-muted-foreground ml-1">/{total}</span>
            )}
          </span>
        );
      },
    },
    {
      key: "scheduledAt",
      header: "Запланировано",
      sortable: true,
      render: (value) => formatDate(value as string | null),
    },
    {
      key: "createdAt",
      header: "Создано",
      sortable: true,
      render: (value) => formatDate(String(value)),
    },
  ];

  const handleDelete = () => {
    if (deleteItems.length === 0) { return; }
    for (const item of deleteItems) {
      deleteBroadcast({ resource: 'broadcasts', id: item.id, successNotification: false });
    }
    notify.success(`Удалено ${deleteItems.length} рассылок`);
    setDeleteItems([]);
  };

  const handleCancel = (items: Broadcast[]) => {
    const ids = items.map((b) => b.id);
    cancelMutate(
      {
        url: '/admin/broadcasts/bulk',
        method: 'post',
        values: { ids, action: 'cancel' },
      },
      {
        onSuccess: () => {
          notify.success('Рассылки отменены');
          listResult.query.refetch();
        },
        onError: () => {
          notify.error('Не удалось отменить рассылки');
        },
      },
    );
  };

  const bulkActions: BulkAction<Broadcast>[] = [
    {
      label: "Просмотр",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/broadcasts/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Отменить",
      icon: <Ban className="h-4 w-4" />,
      onClick: handleCancel,
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Рассылки"
        description="Управление массовыми сообщениями"
        actions={(
          <Button onClick={() => go({ to: "/broadcasts/create" })}>
            <Plus className="h-4 w-4 mr-2" />
            Создать рассылку
          </Button>
        )}
        sticky
      />

      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Поиск по заголовку..."
          className="flex-1"
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value === 'ALL' ? '' : value as BroadcastStatus);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Все статусы</SelectItem>
            <SelectItem value="DRAFT">Черновик</SelectItem>
            <SelectItem value="SCHEDULED">Запланирована</SelectItem>
            <SelectItem value="SENDING">Отправка</SelectItem>
            <SelectItem value="COMPLETED">Завершена</SelectItem>
            <SelectItem value="FAILED">Ошибка</SelectItem>
            <SelectItem value="CANCELLED">Отменена</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(broadcast) =>
          go({ to: `/broadcasts/show/${broadcast.id}` })
        }
        emptyMessage="Нет рассылок"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1 ? 'Удалить рассылку?' : `Удалить ${deleteItems.length} рассылок?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1
                ? 'Вы уверены, что хотите удалить эту рассылку? Это действие нельзя отменить.'
                : `Вы уверены, что хотите удалить ${deleteItems.length} рассылок? Это действие нельзя отменить.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
